<script setup lang="ts">
import * as _ from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { ComponentPublicInstance, computed, onBeforeMount, ref, toRefs, useTemplateRef, watch } from 'vue';
import { DateTimeSchema, MessageSchema, NumberSchema } from '@repo/vue/locales';
import Button from 'primevue/button';
import { Modal } from 'jenesius-vue-modal';
import { ResourceConfig } from '@repo/core/resources';
import { useDxModal } from '@repo/vue/utils';

// locale
const { t } = useI18n<[MessageSchema, DateTimeSchema, NumberSchema]>();

// types
interface DxModalProps {
  html: string | undefined;
  header: string | undefined;
  btnOk?: string | undefined;
  btnCancel?: string | undefined;
  type?: string | undefined;
  size?: string | undefined;
  helpRoute?: string | undefined;
}

interface DxModalParams {
  content?: string;
  title?: string;
  btnOk?: string;
  btnCancel?: string;
}

// props
const props = withDefaults(defineProps<DxModalProps>(), {
  html: 'Without html',
  header: 'Without header',
  type: 'confirm',
  size: 'md'
});
const { html, header, btnOk, btnCancel, type, helpRoute } = toRefs(props);

// emit
const emit = defineEmits([Modal.EVENT_PROMPT, 'confirm']);

// data
const btnOkRef = useTemplateRef<typeof Button & ComponentPublicInstance>('btnOkRef');
const visible = ref<boolean>(true);
const params = ref<DxModalParams>({});

// computed
const modalClass = computed(() => type.value);

// utils
const dxModal = useDxModal();

// methods
const openHelp = () => {
  window.open(`${ResourceConfig.helpBaseUrl}${helpRoute.value}`, '_blank');
};

const confirm = (choice: boolean) => {
  dxModal.pop();
  emit(Modal.EVENT_PROMPT, choice);
  emit('confirm', choice);
};

const showHelpButton = () => !_.isNil(helpRoute.value);

// watches
watch(btnOkRef, (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    btnOkRef.value?.$el.focus();
  }
});

// watches
onBeforeMount(() => {
  switch (type.value) {
    case 'info':
      params.value = {
        title: header.value || t('message.info'),
        content: html.value,
        btnOk: btnOk.value || t('message.ok'),
        btnCancel: btnCancel.value
      };
      break;
    case 'confirm':
      params.value = {
        title: header.value || t('message.confirm'),
        content: html.value,
        btnOk: btnOk.value || t('message.ok'),
        btnCancel: btnCancel.value
      };
      break;
    case 'warning':
      params.value = {
        title: header.value || t('message.warning'),
        content: html.value,
        btnOk: btnOk.value || t('message.ok'),
        btnCancel: btnCancel.value
      };
      break;
    case 'error':
      params.value = {
        title: header.value || t('message.error'),
        content: html.value,
        btnOk: btnOk.value || t('message.ok'),
        btnCancel: btnCancel.value
      };
      break;
  }
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :modal="true"
    :class="modalClass"
    :header="params.title"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '600px' }"
    :close-on-escape="false"
    @after-hide="confirm(false)"
  >
    <span v-html="params.content"></span>

    <template #footer>
      <Button v-if="!_.isEmpty(params.btnOk)" ref="btnOkRef" :label="params.btnOk" @click="confirm(true)" />
      <Button v-if="params.btnCancel" :label="params.btnCancel" @click="confirm(false)" />
    </template>
  </Dialog>
</template>

<style lang="scss"></style>
